import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save, Phone, ExternalLink, Copy, Webhook, Search } from 'lucide-react';
import { api } from '@/api/client';

interface IvrCfg {
  base_url?: string; recording_base_url?: string; username?: string; password?: string;
  from_number_1?: string; from_number_2?: string; webhook_token?: string; enabled?: boolean;
}
interface CallRow {
  id: string; toNumber: string; fromNumber: string; ref?: string; orderId?: string; awbNo?: string; lrNo?: string;
  reason?: string; actorUserId?: number; status: string; responseStatus?: number; responseBody?: string;
  recordingUrl?: string; completedAt?: string; createdAt: string;
  direction?: string; durationSec?: number | null; matchedVia?: string | null; providerCallId?: string | null;
}
interface WebhookField { name: string; required: boolean; aliases: string[]; note: string }
interface WebhookInfo {
  url: string; primaryMethod?: string; methods: string[]; contentType: string; tokenRequired: boolean;
  fields: WebhookField[]; samplePost: Record<string, any>; sampleGet: string; behaviour: string[];
}

function secs(v?: number | null) {
  if (v == null) return '—';
  const m = Math.floor(v / 60), s = v % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
}

export function IvrSettingsPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['settings', 'ivr_details'],
    queryFn: async () => {
      try { return (await api.get<IvrCfg>('/settings/ivr_details')).data ?? {}; }
      catch { return {} as IvrCfg; }
    },
  });
  const { register, handleSubmit, reset, formState: { isDirty } } = useForm<IvrCfg>({ defaultValues: data ?? {} });
  useEffect(() => { if (data) reset(data); }, [data, reset]);

  const save = useMutation({
    mutationFn: async (d: IvrCfg) => (await api.put('/settings/ivr_details', d)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings', 'ivr_details'] }); toast.success('IVR config saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  const [days, setDays] = useState(7);
  const [search, setSearch] = useState('');
  const [recOnly, setRecOnly] = useState(false);
  const { data: calls } = useQuery({
    queryKey: ['ivr-calls', days, search, recOnly],
    queryFn: async () => (await api.get<{ days: number; total: number; items: CallRow[] }>('/ivr/calls', {
      params: { days, ...(search.trim() ? { search: search.trim() } : {}), ...(recOnly ? { withRecordingOnly: 'true' } : {}) },
    })).data,
  });

  // The exact contract to hand the IVR vendor — generated by the backend so it can never drift
  // from what the endpoint actually accepts.
  const { data: hook } = useQuery({
    queryKey: ['ivr-webhook-info'],
    queryFn: async () => (await api.get<WebhookInfo>('/ivr/webhook-info')).data,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-5 max-w-3xl">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><Phone size={18} /> IVR Click-to-Call</h2>
          <p className="text-sm text-slate-500 mt-0.5">Connects to prpmobility for one-click call-back from NDR / Delivered / RTO order pages.</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Base URL">
            <input {...register('base_url')} placeholder="https://prpmobility.com/Api/ClickCallReq.aspx" className="input" />
          </Field>
          <Field label="Recording Base URL">
            <input {...register('recording_base_url')} placeholder="https://prpmobility.com" className="input" />
          </Field>
          <Field label="Username">
            <input {...register('username')} placeholder="20240219" className="input" />
          </Field>
          <Field label="Password">
            <input {...register('password')} type="password" placeholder="••••••••" className="input" />
          </Field>
          <Field label="From Number 1">
            <input {...register('from_number_1')} placeholder="8130002434" className="input font-mono" />
          </Field>
          <Field label="From Number 2">
            <input {...register('from_number_2')} placeholder="8130002427" className="input font-mono" />
          </Field>
          <Field label="Webhook Token (optional)">
            <input {...register('webhook_token')} placeholder="Leave blank to accept unauthenticated pushes" className="input font-mono" />
          </Field>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-600">
          <b>Click-to-call</b> rings the agent first on their <b>own IVR mobile</b> (set per user under Users →
          IVR Mobile) and falls back to From&nbsp;Number&nbsp;1 when the user has none. The provider is called as
          <code className="bg-white px-1 py-0.5 rounded mx-1">?uname=&amp;pass=&amp;reqid=&amp;aparty=&amp;bparty=</code>.
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
          <button type="button" onClick={() => reset(data ?? {})} disabled={!isDirty}
            className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50">Reset</button>
          <button type="submit" disabled={save.isPending || !isDirty}
            className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2 text-sm">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save IVR Config
          </button>
        </div>
      </form>

      {hook && <RecordingWebhookCard info={hook} />}

      <div>
        <div className="flex items-center gap-3 flex-wrap mb-2">
          <div className="text-sm font-semibold flex items-center gap-2"><Phone size={14} /> Call &amp; Recording Log · {calls?.total ?? 0} calls</div>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search mobile / AWB / LR / ref"
              className="border border-slate-200 rounded pl-7 pr-2 py-1.5 text-xs w-60" />
          </div>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="border border-slate-200 rounded px-2 py-1.5 text-xs">
            <option value={1}>Today</option><option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option><option value={90}>Last 90 days</option>
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input type="checkbox" checked={recOnly} onChange={(e) => setRecOnly(e.target.checked)} className="rounded" />
            With recording only
          </label>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Agent</th>
                <th className="text-left px-3 py-2">Ref</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Duration</th>
                <th className="text-left px-3 py-2">Recording</th>
              </tr>
            </thead>
            <tbody>
              {(!calls || calls.items.length === 0) && <tr><td colSpan={9} className="text-center py-4 text-slate-400">No calls in window</td></tr>}
              {calls?.items.map(c => (
                <tr key={c.id} className="border-t">
                  <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{new Date(c.createdAt).toLocaleString('en-IN')}</td>
                  <td className="px-3 py-1.5 font-mono">{c.toNumber}</td>
                  <td className="px-3 py-1.5 font-mono text-slate-500">{c.fromNumber || '—'}</td>
                  <td className="px-3 py-1.5 font-mono text-[10px]">{c.awbNo ?? c.lrNo ?? c.ref ?? '—'}</td>
                  <td className="px-3 py-1.5">
                    {c.direction === 'INBOUND'
                      ? <span className="text-[10px] px-2 py-0.5 rounded bg-indigo-50 text-indigo-700" title={c.matchedVia ? `Matched via ${c.matchedVia}` : undefined}>IVR push</span>
                      : <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">Click-to-call</span>}
                  </td>
                  <td className="px-3 py-1.5">{c.reason ?? '—'}</td>
                  <td className="px-3 py-1.5">
                    <span className={`text-[10px] px-2 py-0.5 rounded ${
                      c.status === 'COMPLETED' || c.status === 'CONNECTED' ? 'bg-emerald-50 text-emerald-700' :
                      c.status === 'FAILED' || c.status === 'NO_ANSWER' || c.status === 'BUSY' ? 'bg-rose-50 text-rose-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>{c.status}</span>
                  </td>
                  <td className="px-3 py-1.5 text-right text-slate-600">{secs(c.durationSec)}</td>
                  <td className="px-3 py-1.5">{c.recordingUrl ? <a target="_blank" rel="noopener noreferrer" href={c.recordingUrl} className="text-indigo-600 inline-flex items-center gap-0.5">Play <ExternalLink size={10} /></a> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/**
 * The block to hand the IVR team: the push URL, the field names accepted (with aliases), ready-made
 * POST/GET samples, and the matching rule — a push whose mobile isn't in our database is dropped.
 */
function RecordingWebhookCard({ info }: { info: WebhookInfo }) {
  const copy = (text: string, what: string) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${what} copied`), () => toast.error('Copy failed'));
  };
  const curl = `curl '${info.sampleGet}'`;

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden max-w-4xl">
      <div className="px-4 py-3 border-b border-slate-100 bg-indigo-50/60">
        <div className="font-semibold text-sm flex items-center gap-2 text-indigo-900"><Webhook size={15} /> Recording Webhook — share this with your IVR team</div>
        <p className="text-xs text-indigo-800/80 mt-0.5">
          Your provider calls this URL with the call outcome + recording as <b>GET query parameters</b>. We store
          it <b>only</b> when the customer number already exists in our database; anything else is acknowledged
          and discarded.
        </p>
      </div>

      <div className="p-4 space-y-4 text-xs">
        <div>
          <div className="text-slate-500 mb-1">
            Push URL <span className="text-emerald-700 font-medium">GET</span>
            <span className="text-slate-400"> — send the fields as query parameters (a JSON POST body is also accepted)</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-slate-50 border border-slate-200 rounded px-2 py-1.5 font-mono break-all">{info.url}</code>
            <button type="button" onClick={() => copy(info.url, 'Webhook URL')}
              className="px-2 py-1.5 border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1 whitespace-nowrap"><Copy size={11} /> Copy</button>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 bg-slate-900 text-slate-100 rounded px-2 py-1.5 font-mono break-all">{info.sampleGet}</code>
            <button type="button" onClick={() => copy(info.sampleGet, 'Full GET URL')}
              className="px-2 py-1.5 border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1 whitespace-nowrap"><Copy size={11} /> Copy</button>
          </div>
          {info.tokenRequired && (
            <div className="mt-1.5 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              A <code>token</code> query parameter is required — send the Webhook Token saved above on every call.
            </div>
          )}
        </div>

        <div>
          <div className="text-slate-500 mb-1">Fields we accept</div>
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 text-slate-500">
                <tr><th className="text-left px-2 py-1.5">Field</th><th className="text-left px-2 py-1.5">Also accepted as</th><th className="text-left px-2 py-1.5">Meaning</th></tr>
              </thead>
              <tbody>
                {info.fields.map((f) => (
                  <tr key={f.name} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 font-mono whitespace-nowrap">
                      {f.name}{f.required && <span className="text-rose-600 ml-0.5" title="required">*</span>}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-slate-500">{f.aliases.join(', ') || '—'}</td>
                    <td className="px-2 py-1.5 text-slate-600">{f.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-slate-500">Ready-to-run test (GET · curl)</span>
              <button type="button" onClick={() => copy(curl, 'curl command')}
                className="text-slate-500 hover:text-slate-800 flex items-center gap-1"><Copy size={11} /> Copy</button>
            </div>
            <pre className="bg-slate-900 text-slate-100 rounded p-2.5 overflow-x-auto font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-all">{curl}</pre>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-slate-500">Optional POST body (only if your provider can't do GET)</span>
              <button type="button" onClick={() => copy(JSON.stringify(info.samplePost, null, 2), 'Sample payload')}
                className="text-slate-500 hover:text-slate-800 flex items-center gap-1"><Copy size={11} /> Copy</button>
            </div>
            <pre className="bg-slate-900 text-slate-100 rounded p-2.5 overflow-x-auto font-mono text-[11px] leading-relaxed">{JSON.stringify(info.samplePost, null, 2)}</pre>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded p-2.5">
          <div className="font-medium text-slate-700 mb-1">What happens to each push</div>
          <ul className="list-disc pl-4 space-y-0.5 text-slate-600">
            {info.behaviour.map((b) => <li key={b}>{b}</li>)}
            <li>A recording sent as a bare filename is turned into a full URL using the <b>Recording Base URL</b> above.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-1"><label className="text-xs font-medium text-slate-600">{label}</label>{children}</div>;
}
